import {
  Button,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { Box, fontWeight, style } from '@mui/system';
import { Styles } from '@t2c/shared-fe';
import { List } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SubscriptionPlan } from '../../hooks/subscription-plans/useGetSubscriptionPlans';
import { useGoogleLoginHandler } from '@t2c/auth';
import { useGoogleLogin } from '../../hooks';
import LoadingButton from '@mui/lab/LoadingButton';
import { useEffect, useRef, useState } from 'react';
import { isAgentPlan, isAgentTrialPlan } from '../public-pages/PricingPage';

const features = [
  {
    text: 'AI video generation',
    subtext:
      'Access over 30 models, including Seedance 2.5, Kling 3.0 Pro, Google Veo 3.1, Kling 2.6 Pro, RunwayML, Luma, Wan, Hailuo, and PixVerse',
  },
  {
    text: 'AI image generation',
    subtext:
      'Access over 30 models, including Nano Banana 2, Nano Banana Pro, GPT-Image-2, USO, Flux, Seedream, Recraft, Stability AI, and Qwen',
  },
  {
    text: 'AI Voice Studio',
    subtext: 'AI voice cloning via ElevenLabs and Fish.audio, with access to custom voices',
  },
  {
    text: 'AI Multi-model Chatbot',
    subtext:
      'Access over 10 models, including Gemini Pro 3, GPT 5.4 Pro, Claude 4, DeepSeek V3, Llama 4, Perplexity Sonar, Grok 4',
  },
  { text: 'note: New Chat, Image, Audio, and Video Models Are Constantly Added' },
  { text: '', isPlan: true },
  { text: 'Lip-sync with Heygen and Hedra, featuring custom avatars' },
  { text: 'AI-powered captioning and optimization' },
  { text: 'AI-based video clipping tool' },
  { text: 'Topaz video upscaling' },
  { text: 'Advanced timeline editor' },
  { text: 'Priority customer support' },
];

const aiStudioFeatures = [
  {
    text: 'Every model runs agentically in the background',
    subtext:
      'Describe your goal once and the agent plans, routes, and executes generation workflows automatically',
  },
  { text: '', isPlan: true },
  {
    text: 'Access 30+ AI Video Models',
    subtext:
      'Including Seedance 2.5, Kling 3.0 Pro, Google Veo 3.1, Kling 3 Motion Control, RunwayML, and more',
  },
  {
    text: '20 social videos or 50 UGC clips in one click',
    subtext: 'Batch-style agent execution for high-volume outputs with minimal manual setup',
  },
  {
    text: 'Full campaigns hands-free - parallel, instant',
    subtext:
      'The agent can run image, video, voice, and lipsync workflows in parallel to accelerate campaign delivery',
  },
  {
    text: 'Priority agent queue + dedicated support',
    subtext: 'Faster processing priority for agent requests plus dedicated help when you need it',
  },
  {
    text: 'Need more credits? Top up for $5 anytime',
    subtext: 'Scale instantly with on-demand credit top-ups directly from your dashboard',
  },
  { text: 'note: Agent models and capabilities are updated constantly' },
  {
    text: 'Agent-led avatar customization and editing',
    subtext:
      'Handle product placement, background replacement, pose variations, and scene compositions through guided agent flows',
  },
  {
    text: 'Agent-first UGC and promotional flow',
    subtext:
      'Built for end-to-end UGC campaign creation with product-aware intake, planning, and generation',
  },
  {
    text: 'MCP Server',
    subtext:
      "Connect with Claude, Codex, Cursor, Antigravity, OpenClaw, and other MCP-compatible AI agents via txt2create's built-in MCP",
  },
];

type PlanFeature = {
  text: string;
  subtext?: string;
  isPlan?: boolean;
};

const isNoteFeature = (feature: PlanFeature) =>
  typeof feature.text === 'string' && feature.text.toLowerCase().startsWith('note:');

const CreditsTooltip = ({ additionalCredits = 5 }) => (
  <Box sx={{ p: 1 }}>
    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>How do credits work?</Typography>

    <Typography sx={{ fontSize: 13, color: '#9CA3AF', mb: 1 }}>
      TXT2Create uses a credits-based system to allocate AI resources fairly. Credits reflect the
      computational load for each AI request.
    </Typography>

    <Typography sx={{ fontSize: 13, mb: 1, color: '#A7F3D0' }}>
      💡 Fun fact: Over 90% of users have plenty left over each month, even with regular use.
    </Typography>

    <Box component="ul" sx={{ pl: 2, m: 0, color: '#9CA3AF' }}>
      <Typography component="li" sx={{ fontSize: 13 }}>
        Unused credits accumulate in your account
      </Typography>
      <Typography component="li" sx={{ fontSize: 13 }}>
        Get additional credits for ${additionalCredits} in your dashboard at any time
      </Typography>
    </Box>
  </Box>
);

const CreditsInfo = ({ interval, additionalCredits, creditsInMillion, isAgentPlan = false }) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {interval === 'Monthly' && (
        <Typography
          sx={{
            ...styles.descriptionText,
            fontSize: '14px',
            fontWeight: 500,
            textAlign: 'left',
          }}
        >
          {isAgentPlan ? (
            <>
              <u>Everything Agent plan includes {creditsInMillion} Million Credits per month</u>
            </>
          ) : (
            <>
              <u>All Premium Models & Features with {creditsInMillion} Million Credits</u>
            </>
          )}
          <Tooltip
            title={<CreditsTooltip additionalCredits={additionalCredits} />}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            componentsProps={{
              tooltip: {
                sx: {
                  borderRadius: 2,
                },
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: '#60A5FA',
                fontSize: 18,
                mx: 0.5,
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
            />
          </Tooltip>
          Dropped Monthly To Use - Access AI Video clippers, all advanced features, premium &
          private models, timeline editors, and so much more.
        </Typography>
      )}

      {interval === 'Yearly' && (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            textAlign: 'left',
          }}
        >
          {isAgentPlan ? (
            <>
              <u>Everything Agent plan includes {creditsInMillion * 12} Million Credits per year</u>
            </>
          ) : (
            <>
              <u>
                All Premium Models & Features with {creditsInMillion * 12} Million Credits Dropped
              </u>
            </>
          )}
          <Tooltip
            title={<CreditsTooltip additionalCredits={5} />}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            componentsProps={{
              tooltip: {
                sx: {
                  borderRadius: 2,
                },
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: '#60A5FA',
                fontSize: 18,
                mx: 0.5,
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
            />
          </Tooltip>
          {isAgentPlan
            ? 'Annually To Use - Everything Agent with autonomous multi-model execution, priority agent queue, hands-free campaigns, and unlimited agentic generations.'
            : 'Annually To Use - Access AI Video clippers, all advanced features, premium & private models, timeline editors, and so much more.'}
        </Typography>
      )}

      {interval === 'Lifetime' && (
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            textAlign: 'left',
          }}
        >
          <u>
            {isAgentPlan
              ? `Everything Agent plan includes ${creditsInMillion} Million Credits in year one, then 216 Million Credits annually`
              : `All Premium Models & Features with ${creditsInMillion} Million Credits in the first year, then 72 Million credits`}
          </u>
          <Tooltip
            title={<CreditsTooltip additionalCredits={5} />}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            componentsProps={{
              tooltip: {
                sx: {
                  borderRadius: 2,
                },
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: '#60A5FA',
                fontSize: 18,
                mx: 0.5,
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
            />
          </Tooltip>
          Annually for life - Access AI Video clippers, all advanced features, premium & private
          models, timeline editors, and so much more.
        </Typography>
      )}
    </>
  );
};

const PlanInfo = ({ creditsInMillion = 0, data }) => {
  const isMonthlySpecial = data.planUniqueId === 'special_7_days_trial_all_access_monthly';
  const isYearlySpecial = data.planUniqueId === 'special_7_days_trial_all_access_yearly';
  const isLifeTimeSpecial = data.planUniqueId === 'lifetime_plan_150m';
  const isAgentPlan = data.planUniqueId.includes('agent');
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const items: PlanFeature[] = isAgentPlan ? aiStudioFeatures : features;
  const visibleFeatureItems = items.filter((feature) => !feature.isPlan && !isNoteFeature(feature));
  const visibleItems = isMobile && !showAllFeatures ? visibleFeatureItems.slice(0, 2) : items;
  const hasMoreItems = isMobile && visibleFeatureItems.length > 2;

  useEffect(() => {
    if (isMobile) {
      setShowAllFeatures(false);
    }
  }, [isMobile]);

  const renderFeatureItem = (feature: PlanFeature, index: number) => {
    const isNote = isNoteFeature(feature);

    return (
      <ListItem key={index} sx={{ pl: 0, pr: 0, pt: '5px', pb: '16px', alignItems: 'flex-start' }}>
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, mt: 0.5 }}>
          {isNote ? (
            <InfoOutlinedIcon sx={{ color: '#60A5FA', fontSize: 18 }} />
          ) : (
            <CheckCircleIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            isNote ? (
              <Typography
                fontSize={13}
                fontWeight={600}
                color="#A7F3D0"
                sx={{ fontStyle: 'italic' }}
              >
                {feature.text.replace(/^note:\s*/i, 'Note: ')}
              </Typography>
            ) : feature.isPlan ? (
              <CreditsInfo
                creditsInMillion={creditsInMillion}
                isAgentPlan={isAgentPlan}
                interval={
                  isLifeTimeSpecial
                    ? 'Lifetime'
                    : isYearlySpecial
                    ? 'Yearly'
                    : isMonthlySpecial
                    ? 'Monthly'
                    : data.interval
                }
                additionalCredits={5}
              />
            ) : (
              <Typography fontSize={14} fontWeight={500} color="white">
                {feature.text}
              </Typography>
            )
          }
          secondary={
            !isNote &&
            feature.subtext && (
              <Typography fontSize={14} color="#9CA3AF">
                {feature.subtext}
              </Typography>
            )
          }
          sx={{ m: 0 }}
        />
      </ListItem>
    );
  };

  return (
    <Box>
      <List sx={{ width: '100%', pb: 0 }}>
        {visibleItems.map((feature, index) => renderFeatureItem(feature, index))}

        {isMobile && hasMoreItems && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 1 }}>
            <Button
              variant="text"
              onClick={() => setShowAllFeatures((current) => !current)}
              sx={{
                color: '#A7F3D0',
                fontWeight: 700,
                textTransform: 'none',
                px: 0,
                minWidth: 'auto',
              }}
            >
              {showAllFeatures ? 'Show less' : 'Show more'}
            </Button>
          </Box>
        )}
      </List>
    </Box>
  );
};

interface Props {
  data: SubscriptionPlan;
  isPublic?: boolean;
  handleSubmit: () => void;
  currentPlanIds?: String[];
  isMostPopular?: boolean;
  isLoading?: boolean;
  showCrossIcon?: boolean;
  isAlphaPricingUniqueId?: string;
  activeTrialPlanUniqueId: string | null;
  activeTrialPlanUniqueIds?: string[];
  enableAgentPlan?: boolean;
  isTrialPlanPurchasable: boolean;
  /**
   * When rendering on a public page (isPublic=true), require an auth (Google) login
   * before running handleSubmit. Default true to preserve PricingPage behavior.
   * Set to false (e.g. ExpressCheckout) to allow proceeding to guest/email flow.
   */
  requireAuthForPublic?: boolean;
  /**
   * Enables special-agent-plan pricing presentation for yearly cards only.
   */
  isSpecialAgentPlanMode?: boolean;
}
const PlanCard = ({
  data,
  isPublic,
  handleSubmit,
  currentPlanIds,
  isMostPopular,
  isLoading,
  showCrossIcon,
  isAlphaPricingUniqueId,
  activeTrialPlanUniqueId = null,
  activeTrialPlanUniqueIds = [],
  isTrialPlanPurchasable = false,
  requireAuthForPublic = true,
  enableAgentPlan = false,
  isSpecialAgentPlanMode = false,
}: Props) => {
  const {
    handleGoogleLoginSuccess,
    handleOnError,
    isLoading: isLogging,
    isLoggedIn,
  } = useGoogleLogin({ shouldRedirectOnSuccess: false });
  const { handleLogin } = useGoogleLoginHandler({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleOnError,
  });

  const submissionPendingRef = useRef(false);

  // Check if logged in changed in an effect
  useEffect(() => {
    // If we're now logged in and we have a pending submission
    if (isLoggedIn && submissionPendingRef.current) {
      // Clear the pending flag
      submissionPendingRef.current = false;
      // Call handleSubmit without triggering a state update
      handleSubmit();
    }
    // We deliberately exclude handleSubmit to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleLoginSubmit = () => {
    try {
      // Persist desired post-login redirect and plan data for public pricing flows
      if (isPublic && requireAuthForPublic) {
        try {
          window.sessionStorage.setItem('post_login_redirect', window.location.pathname);
          window.sessionStorage.setItem(
            'post_login_plan',
            JSON.stringify({
              id: data.id,
              amount: data.amount,
              discount: data.discountPercent,
              interval: data.interval,
              name: data.name,
              ts: Date.now(),
            })
          );
        } catch {}
      }
      // Set ref to track that we want to submit after login
      submissionPendingRef.current = true;
      handleLogin();
    } catch (error) {
      console.error('Login failed:', error);
      // Clear the pending flag if login fails
      submissionPendingRef.current = false;
    }
  };

  const discountedPrice = Math.round(
    data?.amount - data?.amount * ((data?.discountPercent || 0) / 100)
  );

  const LIFETIME_PLAN_UNIQUE_ID = 'lifetime_plan_150m';
  const isLifetimePlan = data?.planUniqueId === LIFETIME_PLAN_UNIQUE_ID;
  const isSpecialTrialPlan =
    data?.interval === 'Special' &&
    (data?.planUniqueId === 'special_7_days_trial_all_access_yearly' ||
      data?.planUniqueId === 'special_7_days_trial_all_access_monthly' ||
      isAgentTrialPlan(data?.planUniqueId));

  const isYearly = data?.interval === 'Yearly';
  const shouldUseSpecialYearlyDisplay =
    isSpecialAgentPlanMode && isYearly && !isSpecialTrialPlan && !isLifetimePlan;
  const monthlyPrice =
    isAlphaPricingUniqueId === 'special_7_days_trial_all_access_yearly' ||
    isAlphaPricingUniqueId === 'special_7_days_trial_agent_yearly'
      ? Math.round(data.amount / 12)
      : isAlphaPricingUniqueId === 'special_7_days_trial_all_access_monthly' ||
        isAlphaPricingUniqueId === 'special_7_days_trial_agent_monthly'
      ? data.amount
      : isYearly
      ? Math.round(discountedPrice / 12)
      : data?.amount;
  const creditsInMillion = Math.round((data?.credits || 0) / 1000000);

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const isThisCardAgentPlan = isAgentPlan(data?.planUniqueId);
  const isTrialIdActive = (trialPlanUniqueId: string) =>
    activeTrialPlanUniqueId === trialPlanUniqueId ||
    activeTrialPlanUniqueIds.includes(trialPlanUniqueId);
  const isAnyTrialActive = !!activeTrialPlanUniqueId || activeTrialPlanUniqueIds.length > 0;
  const isAgentPricingCard = enableAgentPlan && isThisCardAgentPlan;

  // Match trial status to the same plan category so "Pay Now" only applies to the correct type
  const isMatchingYearlyTrial = isThisCardAgentPlan
    ? isTrialIdActive('special_7_days_trial_agent_yearly')
    : isTrialIdActive('special_7_days_trial_all_access_yearly');
  const isMatchingMonthlyTrial = isThisCardAgentPlan
    ? isTrialIdActive('special_7_days_trial_agent_monthly')
    : isTrialIdActive('special_7_days_trial_all_access_monthly');
  const isMatchingTrialActive = isMatchingYearlyTrial || isMatchingMonthlyTrial;

  const shouldDisableSubscribeButton =
    !!showCrossIcon ||
    (!isTrialPlanPurchasable && isSpecialTrialPlan) ||
    // Only disable for "Trial Plan Activated" (yearly trial active and monthly plan of same category)
    (isMatchingYearlyTrial && data.interval === 'Monthly');

  const getTrialAwareCtaLabel = (): string => {
    if (!isAnyTrialActive || !isMatchingTrialActive) return '';

    if (isMatchingYearlyTrial) {
      if (data.interval === 'Monthly') return 'Trial Plan Activated';
      if (data.interval === 'Yearly') return 'Pay Now';
      return '';
    }

    if (isMatchingMonthlyTrial) {
      if (data.interval === 'Monthly' || data.interval === 'Yearly') return 'Pay Now';
      return '';
    }

    return '';
  };

  return (
    <Box
      sx={{
        ...styles.card,
        ...(enableAgentPlan && isExtraSmall
          ? {
              boxShadow: 'inset -2px -1px 1px 0px #f9f9f96e, inset 1px 1px 1px 0px #f9f9f994',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              borderStyle: 'solid',
              margin: '8px',
              padding: '16px 10px 8px',
            }
          : isExtraSmall
          ? {
              padding: '16px 10px 8px',
            }
          : {
              boxShadow: 'inset -2px -1px 1px 0px #f9f9f96e, inset 1px 1px 1px 0px #f9f9f994',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              borderStyle: 'solid',
              margin: '12px',
              padding: '20px 22px',
            }),
      }}
    >
      <Box
        sx={{
          ...styles.headerRow,
          flexDirection: enableAgentPlan || isExtraSmall ? 'column' : 'row',
          justifyContent: enableAgentPlan || isExtraSmall ? 'center' : 'space-between',
          alignItems: enableAgentPlan || isExtraSmall ? 'center' : 'flex-start',
          marginBottom: enableAgentPlan || isExtraSmall ? 1.5 : 2,
        }}
      >
        {data?.name && enableAgentPlan && (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: '#A78BFA',
              mb: 1,
              width: '100%',
              textAlign: enableAgentPlan || isExtraSmall ? 'center' : 'left',
            }}
          >
            {data.name}
          </Typography>
        )}
        <Box
          sx={{
            ...styles.priceContainer,
            textAlign: enableAgentPlan || isExtraSmall ? 'center' : undefined,
            alignItems: enableAgentPlan || isExtraSmall ? 'center' : 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            {data?.interval === 'Yearly' && (
              <Typography sx={styles.oldPrice}>
                {shouldUseSpecialYearlyDisplay ? `$997` : `$${(monthlyPrice * 3).toFixed(2)}`}
              </Typography>
            )}
            {data?.interval === 'Monthly' && (
              <Typography sx={styles.oldPrice}>{`$${(data?.amount * 3).toFixed(2)}`}</Typography>
            )}
            <Typography sx={styles.currentPrice}>
              {isSpecialTrialPlan
                ? 'FREE TRIAL'
                : isLifetimePlan
                ? `$${data?.amount}`
                : shouldUseSpecialYearlyDisplay
                ? `$${discountedPrice}`
                : `$${monthlyPrice}`}
            </Typography>
            {data?.interval !== 'Special' && (
              <Typography sx={styles.intervalText}>
                {shouldUseSpecialYearlyDisplay ? '/yr' : '/mo'}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: enableAgentPlan || isExtraSmall ? 'center' : 'flex-start',
              width: '100%',
            }}
          >
            <Typography fontSize={15} fontWeight={400} sx={{ mb: 0.5, textAlign: 'center' }}>
              {isAlphaPricingUniqueId === 'special_7_days_trial_all_access_yearly' ||
              isAlphaPricingUniqueId === 'special_7_days_trial_agent_yearly'
                ? `For 7 Days Then $${data?.amount}/Year`
                : isAlphaPricingUniqueId === 'special_7_days_trial_all_access_monthly' ||
                  isAlphaPricingUniqueId === 'special_7_days_trial_agent_monthly'
                ? `For 7 Days Then $${data?.amount}/Month`
                : isLifetimePlan
                ? `One-time payment`
                : shouldUseSpecialYearlyDisplay
                ? `Private Offer`
                : data?.interval === 'Yearly'
                ? `Billed Annually $${discountedPrice}/Year`
                : 'Billed Monthly'}
            </Typography>
          </Box>

          {/* <Typography sx={styles.creditsText}>
            {data?.interval === 'Special'
              ? `${creditsInMillion} million credits initially`
              : data?.interval === 'Yearly'
              ? `${creditsInMillion * 12} million credits at once`
              : `${creditsInMillion} million credits per month`}
          </Typography> */}
        </Box>

        <Box sx={{ display: enableAgentPlan || isExtraSmall ? 'none' : 'block' }}>
          {isLoading ? (
            <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 2 }} />
          ) : (
            <Box>
              {currentPlanIds.includes(data.id) ? (
                <Button variant="outlined" sx={styles.currentPlanBtn}>
                  Current Plan
                </Button>
              ) : (
                <LoadingButton
                  disabled={shouldDisableSubscribeButton}
                  variant="contained"
                  sx={styles.btn}
                  onClick={
                    !isLoggedIn && (requireAuthForPublic || !isPublic)
                      ? handleLoginSubmit
                      : handleSubmit
                  }
                  loading={isLogging}
                >
                  {getTrialAwareCtaLabel() ||
                    (isPublic
                      ? isLoggedIn
                        ? !isTrialPlanPurchasable && isAlphaPricingUniqueId
                          ? 'Already Claimed'
                          : isLifetimePlan
                          ? 'Buy now'
                          : 'Subscribe'
                        : 'Get Started'
                      : isLifetimePlan
                      ? 'Buy now'
                      : 'Subscribe')}
                </LoadingButton>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: enableAgentPlan || isExtraSmall ? 'flex' : 'none',
          justifyContent: 'center',
          mt: 1,
          mb: 2,
          width: '100%',
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 2 }} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {currentPlanIds.includes(data.id) ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <Box sx={styles.currentPlanSideLines} />
                <Button
                  sx={{
                    ...styles.currentPlanBtn,
                    mx: 2,
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                  }}
                  variant="outlined"
                >
                  Current Plan
                </Button>
                <Box sx={styles.currentPlanSideLines} />
              </Box>
            ) : (
              <LoadingButton
                disabled={shouldDisableSubscribeButton}
                variant="contained"
                sx={styles.btn}
                onClick={
                  !isLoggedIn && (requireAuthForPublic || !isPublic)
                    ? handleLoginSubmit
                    : handleSubmit
                }
                loading={isLogging}
              >
                {getTrialAwareCtaLabel() ||
                  (isPublic
                    ? isLoggedIn
                      ? !isTrialPlanPurchasable && isAlphaPricingUniqueId
                        ? 'Already Claimed'
                        : isLifetimePlan
                        ? 'Buy now'
                        : 'Subscribe'
                      : 'Get Started'
                    : isLifetimePlan
                    ? 'Buy now'
                    : 'Subscribe')}
              </LoadingButton>
            )}
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          ...styles.descriptionText,
          textAlign: enableAgentPlan || isExtraSmall ? 'center' : undefined,
        }}
      >
        {isAgentPricingCard ? (
          <>Autonomous multi-model execution and premium agent workflows.</>
        ) : (
          <>Access to all Premium AI models and features.</>
        )}
      </Typography>

      {/* <Typography
        sx={{ ...styles.subDescriptionText, textAlign: isExtraSmall ? 'center' : undefined }}
      >
        Need more credits? Add 1 million more to your plan inside of your dashboard for only{' '}
        <Typography
          component="span"
          sx={{ fontWeight: 400, color: '#FFFFFF', fontSize: 'clamp(10px, 2.5vw, 12px)' }}
        >
          $5
        </Typography>
      </Typography> */}

      <Divider sx={{ ...styles.divider, my: 1 }} />

      <PlanInfo creditsInMillion={creditsInMillion} data={data} />
    </Box>
  );
};

const styles: Styles = {
  card: {
    width: '100%',
    height: 'auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '24px',
    color: '#fff',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 2,
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  oldPrice: {
    textDecoration: 'line-through',
    color: '#9CA3AF', // Gray-400
    fontSize: 'clamp(14px, 4vw, 18px)',
    fontWeight: 500,
    marginRight: '8px',
  },
  currentPrice: {
    fontSize: 'clamp(32px, 8vw, 48px)',
    fontWeight: 700,
    color: '#FFFFFF',
    lineHeight: 1,
  },
  intervalText: {
    fontSize: 'clamp(18px, 5vw, 24px)',
    color: '#FFFFFF',
    fontWeight: 600,
    marginLeft: '4px',
  },
  creditsText: {
    fontSize: 'clamp(14px, 4vw, 18px)',
    fontWeight: 600,
    color: '#FFFFFF',
  },
  descriptionText: {
    fontSize: 'clamp(14px, 3vw, 16px)',
    color: '#E5E7EB', // Gray-200
    lineHeight: 1.5,
  },
  subDescriptionText: {
    fontSize: 'clamp(14px, 2.5vw, 16px)',
    color: '#9CA3AF', // Gray-400
    marginTop: '6px',
  },
  divider: {
    borderColor: '#4B5563', // Gray-600
  },
  btn: {
    backgroundColor: '#8B5CF6', // Purple-500
    color: '#FFFFFF',
    borderRadius: '12px',
    padding: '10px 24px',
    textTransform: 'none',
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#7C3AED', // Purple-600
      boxShadow: 'none',
    },
    height: 'fit-content',
    minWidth: '140px',
    '&.Mui-disabled': {
      color: '#ffffffff', // Gray-300
    },
  },
  currentPlanBtn: {
    background: 'linear-gradient(82.97deg, #000000 7.07%, #666666 136.49%)',
    boxShadow: '0px 0px 10px rgba(0, 187, 255, 0.75)',
    border: '2px solid rgb(108 175 223 / 75%)',
    color: '#FFFFFF',
    borderRadius: '21px',
    padding: '10px 24px',
    textTransform: 'none',
    fontSize: 'clamp(14px, 3.5vw, 16px)',
    fontWeight: 600,
    height: 'fit-content',
  },
  currentPlanSideLines: {
    borderTop: '1px solid rgba(156,163,175,0.6)',
    flexGrow: 1,
  },
  mostPopular: {
    position: 'absolute',
    right: 10,
    top: -15,
    backgroundColor: '#8B5CF6',
    borderRadius: '30px',
    p: '4px 12px',
    color: '#fff',
    fontSize: 'clamp(14px, 2.5vw, 16px)',
  },
};

export default PlanCard;
